// Воксельные объекты, синхронизированные с сервером.
// Цвет считается шейдером (триплантар по brick.png + depth-shading), поэтому
// per-cube color больше не храним — значительная экономия RAM по сравнению
// с пред-запеканием.
//
//   alive       — packed bitmask (1 бит на слот)
//   visibleList — плотный массив индексов "видимых" (поверхностных) кубиков
//   visibleIdx  — Int32Array, для каждого слота: позиция в visibleList или -1
//   textureName — имя текстуры; "" = uniform color, иначе — шейдер сэмплит uTex.

import type { ChunkInit, ChunkRoleValue } from "../net/protocol";
import { ChunkRole } from "../net/protocol";
import { getTexture, sampleAvg } from "./textures";

// Должно совпадать с uVTileScale в renderer.ts (= 900/550 для brick.png).
const V_TILE_SCALE = 900 / 550;
const TILE_SIZE = 1.0;

export interface VoxelChunkClient {
  id: number;
  ox: number; oy: number; oz: number;
  sx: number; sy: number; sz: number;
  cubeSize: number;
  color: [number, number, number];
  alive: Uint8Array;
  visibleList: number[];
  visibleIdx: Int32Array;
  textureName: string;
  /** Семантическая роль — для classification (collision, shader path, transparency). */
  role: ChunkRoleValue;
  /** 0..1, 0=хрупкий, 1=прочный — используется для разлёта осколков */
  strength: number;
  /** Per-cube UV (full-size sx*sy*sz*4 bytes = u16 u + u16 v). Если null —
   *  обычный triplanar/uniform color. Для интерьерных объектов — !== null. */
  perCubeUV: Uint8Array | null;
  /** Per-face corner UVs: alive_count × 6 граней × 4 угла × 4 байта (u16 u, u16 v).
   *  Загружается на GPU как RG16 lookup-текстура; vert делает texelFetch, frag сэмплит
   *  оригинальную diffuse-текстуру. См. uploadFaceUVs в renderer.ts. */
  faceUVs: Uint8Array | null;
  /** cubeIdx → aliveIdx (порядковый номер среди alive cubes по сканлайн-обходу).
   *  -1 для мёртвых. Нужно для маппинга grid → строки uv-lookup в шейдере. */
  aliveIdx: Int32Array | null;
  /** true = объект подчиняется физике (pose не identity). Рендер должен
   *  трансформировать каждый куб через pose. */
  dynamic: boolean;
  /** true = чанк видим воксельно даже когда есть static_meshes.glb (его
   *  меш вырезан из визуального GLB на этапе build_level). Включается для
   *  нод с явным префиксом `vox_` — единственный путь увидеть разрушение. */
  voxVisual: boolean;
  /** Имя normal-map текстуры (`levelpacks/.../mat_N_n.png`), "" если нет. */
  normalTexture: string;
  /** 0=opaque, 1=mask (alpha-cutout), 2=blend (на voxel пока тоже cutout). */
  alphaMode: number;
  /** Порог discard'а для alphaMode=mask (default 0.5). */
  alphaCutoff: number;
  /** Сила нормал-мапы (gltf normalTexture.scale; 1.0 = default). */
  normalScale: number;
  /** Текущая поза (актуально только для dynamic). Обновляется по сети из
   *  ChunkPoseUpdate событий. Клиент экстраполирует между апдейтами. */
  pose: ChunkPose;
  /** Mesh bbox в локальных coords (для triplanar нормализации в шейдере) */
  bboxMin: [number, number, number];
  bboxSize: [number, number, number];
  /** ColumnTopZ / ColumnBottomZ — per-column height-maps (SX*SY флоатов).
   *  Кэшируются лениво при первом обращении через ensureColumnMaps();
   *  локально пересчитываются по `break()`. Используются клиентским
   *  mirror'ом floorAtColumn/ceilingAtColumn для voxel-column коллизии. */
  columnTopZ: Float32Array | null;
  columnBottomZ: Float32Array | null;
  /** Монотонно растёт когда меняется visibleList (upsert/break). Renderer
   *  использует это как ключ инвалидации per-chunk instance-buffer cache.
   *  ВАЖНО: NOT глобальный contentEpoch, чтобы breaking в одном чанке не
   *  заставлял rebuild'ить инстансы соседних чанков (~2 млн кубов). */
  visibleEpoch: number;
  /** Индексы кубов, чья видимость менялась с прошлого кадра рендера
   *  (выбитый куб + соседи, которые могли обнажиться). Renderer сливает
   *  список и пересобирает ТОЛЬКО задетые тайлы, а не весь чанк. Пустой
   *  список при выросшем visibleEpoch = «не знаем что менялось» → полная
   *  пересборка (upsert, загрузка уровня). */
  dirtyCubes: number[];
}

export const COLUMN_EMPTY_TOP = -1e20;
export const COLUMN_EMPTY_BOTTOM = 1e20;

export interface ChunkPose {
  pos: [number, number, number];
  vel: [number, number, number];
  /** Quaternion (W, X, Y, Z); identity = [1,0,0,0] */
  quat: [number, number, number, number];
  /** Угловая скорость как 3D вектор (rad/s, world space) */
  angVelVec: [number, number, number];
  resting: boolean;
  /** performance.now()/1000 при последнем update — для экстраполяции */
  lastUpdateTime: number;
}

export class ChunkRegistry {
  private byId = new Map<number, VoxelChunkClient>();
  /** Инкремент при upsert и при разрушении куба — для инвалидации GPU-кэшей инстансов. */
  private _contentEpoch = 0;

  /** Монотонно растёт при любых изменениях геометрии чанков на клиенте. */
  contentEpoch(): number {
    return this._contentEpoch;
  }

  upsert(init: ChunkInit): VoxelChunkClient {
    const total = init.sx * init.sy * init.sz;
    const c: VoxelChunkClient = {
      id: init.chunkId,
      ox: init.ox, oy: init.oy, oz: init.oz,
      sx: init.sx, sy: init.sy, sz: init.sz,
      cubeSize: init.cubeSize,
      color: [init.r / 255, init.g / 255, init.b / 255],
      alive: init.alive.slice(),
      visibleList: [],
      visibleIdx: new Int32Array(total).fill(-1),
      textureName: init.texture,
      role: init.role ?? ChunkRole.Decor,
      strength: init.strength / 255,
      perCubeUV: init.uvs ? init.uvs.slice() : null,
      faceUVs: init.faceUVs ? init.faceUVs.slice() : null,
      aliveIdx: init.faceUVs ? buildAliveIdxLUT(init.alive, total) : null,
      dynamic: init.dynamic,
      voxVisual: init.voxVisual,
      normalTexture: init.normalTexture,
      alphaMode: init.alphaMode,
      alphaCutoff: init.alphaCutoff > 0 ? init.alphaCutoff : 0.5,
      normalScale: init.normalScale > 0 ? init.normalScale : 1.0,
      pose: {
        pos: [0, 0, 0],
        vel: [0, 0, 0],
        quat: [1, 0, 0, 0],
        angVelVec: [0, 0, 0],
        resting: true,
        lastUpdateTime: performance.now() / 1000,
      },
      bboxMin: init.bboxMin,
      bboxSize: init.bboxSize,
      columnTopZ: null,
      columnBottomZ: null,
      visibleEpoch: 1,
      dirtyCubes: [],
    };
    rebuildVisible(c);
    this.byId.set(c.id, c);
    this._contentEpoch++;
    this._allDirty = true;
    return c;
  }

  get(id: number): VoxelChunkClient | undefined { return this.byId.get(id); }
  private _allCache: VoxelChunkClient[] = [];
  private _allDirty = true;
  /** Возвращает стабильный массив чанков. Пересоздаётся только при upsert/clear. */
  all(): VoxelChunkClient[] {
    if (this._allDirty) {
      this._allCache = [...this.byId.values()];
      this._allDirty = false;
    }
    return this._allCache;
  }
  clear(): void {
    this.byId.clear();
    this._contentEpoch++;
    this._allDirty = true;
  }

  updatePose(
    chunkId: number,
    pos: [number, number, number], vel: [number, number, number],
    quat: [number, number, number, number],
    angVelVec: [number, number, number],
    resting: boolean, now: number,
  ): void {
    const c = this.byId.get(chunkId);
    if (!c) return;
    c.pose.pos = pos;
    c.pose.vel = vel;
    c.pose.quat = quat;
    c.pose.angVelVec = angVelVec;
    c.pose.resting = resting;
    c.pose.lastUpdateTime = now;
  }

  /** Сбить кубик. Вернёт мировой центр или null если не нашли/уже мёртв. */
  break(chunkId: number, cubeIdx: number): [number, number, number] | null {
    const c = this.byId.get(chunkId);
    if (!c) return null;
    const byte = cubeIdx >> 3;
    const bit = 1 << (cubeIdx & 7);
    if (byte >= c.alive.length) return null;
    if ((c.alive[byte] & bit) === 0) return null;
    c.alive[byte] &= ~bit;
    if (c.columnTopZ !== null) {
      // O(SZ) локальный пересчёт столбца для column-lookup коллизий.
      const bx = cubeIdx % c.sx;
      const by = Math.floor(cubeIdx / c.sx) % c.sy;
      recomputeChunkColumn(c, bx, by);
    }
    removeVisible(c, cubeIdx);
    c.dirtyCubes.push(cubeIdx);
    this._contentEpoch++;
    const sxsy = c.sx * c.sy;
    const bx = cubeIdx % c.sx;
    const by = Math.floor(cubeIdx / c.sx) % c.sy;
    const bz = Math.floor(cubeIdx / sxsy);
    // Соседи заносим в dirtyCubes безусловно: если сосед не обнажился,
    // лишним будет только повторный пересчёт его тайла (дёшево).
    if (bx + 1 < c.sx) { maybeAddVisible(c, cubeIdx + 1);     c.dirtyCubes.push(cubeIdx + 1); }
    if (bx > 0)        { maybeAddVisible(c, cubeIdx - 1);     c.dirtyCubes.push(cubeIdx - 1); }
    if (by + 1 < c.sy) { maybeAddVisible(c, cubeIdx + c.sx);  c.dirtyCubes.push(cubeIdx + c.sx); }
    if (by > 0)        { maybeAddVisible(c, cubeIdx - c.sx);  c.dirtyCubes.push(cubeIdx - c.sx); }
    if (bz + 1 < c.sz) { maybeAddVisible(c, cubeIdx + sxsy);  c.dirtyCubes.push(cubeIdx + sxsy); }
    if (bz > 0)        { maybeAddVisible(c, cubeIdx - sxsy);  c.dirtyCubes.push(cubeIdx - sxsy); }
    c.visibleEpoch++;
    return cubeWorldCenter(c, cubeIdx);
  }
}

/** Lazy compute ColumnTopZ/ColumnBottomZ по текущему alive bitmap.
 *  Зеркало server-side `EnsureColumnMaps`. Размер SX*SY, sentinel
 *  COLUMN_EMPTY_TOP/BOTTOM для "нет живых кубов в столбце". */
export function ensureColumnMaps(c: VoxelChunkClient): void {
  if (c.columnTopZ !== null && c.columnBottomZ !== null) return;
  const sx = c.sx, sy = c.sy, sz = c.sz;
  const sxsy = sx * sy;
  const cs = c.cubeSize;
  const cols = sx * sy;
  const top = new Float32Array(cols);
  const bot = new Float32Array(cols);
  for (let i = 0; i < cols; i++) {
    top[i] = COLUMN_EMPTY_TOP;
    bot[i] = COLUMN_EMPTY_BOTTOM;
  }
  for (let bz = 0; bz < sz; bz++) {
    const zBase = bz * sxsy;
    const topZ = c.oz + (bz + 1) * cs;
    const botZ = c.oz + bz * cs;
    for (let by = 0; by < sy; by++) {
      const yBase = by * sx;
      for (let bx = 0; bx < sx; bx++) {
        const idx = bx + yBase + zBase;
        if ((c.alive[idx >> 3] & (1 << (idx & 7))) === 0) continue;
        const colIdx = bx + by * sx;
        if (topZ > top[colIdx]) top[colIdx] = topZ;
        if (botZ < bot[colIdx]) bot[colIdx] = botZ;
      }
    }
  }
  c.columnTopZ = top;
  c.columnBottomZ = bot;
}

/** Пересчитать столбец (bx, by) после `break`. O(SZ). */
function recomputeChunkColumn(c: VoxelChunkClient, bx: number, by: number): void {
  if (c.columnTopZ === null || c.columnBottomZ === null) return;
  const sx = c.sx, sz = c.sz;
  const sxsy = sx * c.sy;
  const cs = c.cubeSize;
  const colIdx = bx + by * sx;
  let top = COLUMN_EMPTY_TOP;
  let bot = COLUMN_EMPTY_BOTTOM;
  for (let bz = 0; bz < sz; bz++) {
    const idx = bx + by * sx + bz * sxsy;
    if ((c.alive[idx >> 3] & (1 << (idx & 7))) === 0) continue;
    const topZ = c.oz + (bz + 1) * cs;
    const botZ = c.oz + bz * cs;
    if (topZ > top) top = topZ;
    if (botZ < bot) bot = botZ;
  }
  c.columnTopZ[colIdx] = top;
  c.columnBottomZ[colIdx] = bot;
}

/** Один проход по alive-битам: cubeIdx → aliveIdx (порядковый номер в той же
 *  iteration order, в которой voxelize/main.go писал face patches). Мёртвые
 *  cubes получают -1 (для них в шейдере нет патча). Используется в renderer
 *  для маппинга per-instance gridPos → строки патч-атласа. */
function buildAliveIdxLUT(alive: Uint8Array, total: number): Int32Array {
  const lut = new Int32Array(total);
  let n = 0;
  for (let i = 0; i < total; i++) {
    if ((alive[i >> 3] & (1 << (i & 7))) !== 0) {
      lut[i] = n++;
    } else {
      lut[i] = -1;
    }
  }
  return lut;
}

function rebuildVisible(c: VoxelChunkClient): void {
  const sx = c.sx, sy = c.sy, sz = c.sz;
  const sxsy = sx * sy;
  const aliveBytes = c.alive;

  for (let b = 0; b < aliveBytes.length; b++) {
    const byteVal = aliveBytes[b];
    if (byteVal === 0) continue;
    const baseIdx = b << 3;
    for (let bit = 0; bit < 8; bit++) {
      if ((byteVal & (1 << bit)) === 0) continue;
      const idx = baseIdx + bit;
      const bx = idx % sx;
      const by = Math.floor(idx / sx) % sy;
      const bz = Math.floor(idx / sxsy);

      // Проверяем все 6 граней. Грань "exposed" если соседа нет (граница чанка)
      // или сосед мёртв. Универсально работает для тонких (sz=1) чанков —
      // floor/ceiling — у которых верхняя грань всегда exposed.
      let exposed = false;
      if (bx === sx - 1 || !isAliveBit(aliveBytes, idx + 1)) exposed = true;
      else if (bx === 0 || !isAliveBit(aliveBytes, idx - 1)) exposed = true;
      else if (by === sy - 1 || !isAliveBit(aliveBytes, idx + sx)) exposed = true;
      else if (by === 0 || !isAliveBit(aliveBytes, idx - sx)) exposed = true;
      else if (bz === sz - 1 || !isAliveBit(aliveBytes, idx + sxsy)) exposed = true;
      else if (bz === 0 || !isAliveBit(aliveBytes, idx - sxsy)) exposed = true;

      if (exposed) addVisible(c, idx);
    }
  }
}

function addVisible(c: VoxelChunkClient, idx: number): void {
  if (c.visibleIdx[idx] >= 0) return;
  c.visibleIdx[idx] = c.visibleList.length;
  c.visibleList.push(idx);
}

function removeVisible(c: VoxelChunkClient, idx: number): void {
  const pos = c.visibleIdx[idx];
  if (pos < 0) return;
  const lastPos = c.visibleList.length - 1;
  const lastIdx = c.visibleList[lastPos];
  c.visibleList[pos] = lastIdx;
  c.visibleIdx[lastIdx] = pos;
  c.visibleList.pop();
  c.visibleIdx[idx] = -1;
}

function maybeAddVisible(c: VoxelChunkClient, idx: number): void {
  if (!isAliveBit(c.alive, idx)) return;
  if (c.visibleIdx[idx] >= 0) return;
  addVisible(c, idx);
}

export function isAliveBit(alive: Uint8Array, idx: number): boolean {
  return (alive[idx >> 3] & (1 << (idx & 7))) !== 0;
}

/** Вернуть "цвет" кубика для разлетающегося осколка. Если у чанка есть
 *  текстура — сэмплим её по той же триплантар-схеме, что и шейдер
 *  (усреднение прямоугольника по footprint'у кубика). Иначе возвращаем
 *  uniform color чанка. Это нужно чтобы отлетающие гранулы по цвету
 *  совпадали со стеной из которой вылетели. */
export function sampleChunkCubeColor(
  c: VoxelChunkClient,
  cx: number, cy: number, cz: number,
  cubeIdx?: number,
): [number, number, number] {
  // Для интерьерного объекта с per-cube UV сэмплим object texture по UV куба.
  if (c.perCubeUV && cubeIdx !== undefined) {
    const tex = getTexture(c.textureName);
    if (tex) {
      const ui = cubeIdx * 4;
      const u = (c.perCubeUV[ui] | (c.perCubeUV[ui + 1] << 8)) / 65535;
      const v = (c.perCubeUV[ui + 2] | (c.perCubeUV[ui + 3] << 8)) / 65535;
      return sampleAvg(tex, u - 0.005, v - 0.005, u + 0.005, v + 0.005);
    }
    return c.color;
  }
  // vox_visual чанки (стены/полы лабиринта): у кубов есть запечённые corner
  // UV per-face (faceUVs, fixed-point [0,8) — см. build_level faceUVScale).
  // Сэмплим диффуз по центру первой непустой грани — обломки получают цвет
  // РЕАЛЬНОЙ точки текстуры, из которой выбит куб, а не триплан-приближение.
  if (c.faceUVs && c.aliveIdx && cubeIdx !== undefined) {
    const tex = getTexture(c.textureName);
    const ai = c.aliveIdx[cubeIdx];
    if (tex && ai >= 0) {
      for (let f = 0; f < 6; f++) {
        const base = (ai * 24 + f * 4) * 4;
        let u = 0, v = 0, nonZero = false;
        for (let corner = 0; corner < 4; corner++) {
          const o = base + corner * 4;
          const cu = ((c.faceUVs[o] | (c.faceUVs[o + 1] << 8)) / 65535) * 8;
          const cv = ((c.faceUVs[o + 2] | (c.faceUVs[o + 3] << 8)) / 65535) * 8;
          if (cu !== 0 || cv !== 0) nonZero = true;
          u += cu * 0.25;
          v += cv * 0.25;
        }
        if (!nonZero) continue; // грань без проекции (fallback UV 0,0)
        u -= Math.floor(u);
        v -= Math.floor(v);
        return sampleAvg(tex, u - 0.01, v - 0.01, u + 0.01, v + 0.01);
      }
    }
    // Нет текстуры/граней — падаем в триплан-ветку ниже.
  }
  if (!c.textureName) return c.color;
  const tex = getTexture(c.textureName);
  if (!tex) return c.color;
  const scaleU = 1.0 / TILE_SIZE;
  const half = c.cubeSize * 0.5;
  let u0: number, u1: number, v0: number, v1: number;
  // Триплантар: пол (низкая Z) → XY, иначе — стена с U по той оси, что
  // ближе к краю тайла (там и грань стены).
  if (cz < 0.1) {
    u0 = cx * scaleU - half * scaleU;
    u1 = cx * scaleU + half * scaleU;
    v0 = cy * scaleU - half * scaleU;
    v1 = cy * scaleU + half * scaleU;
  } else {
    const fx = cx - Math.floor(cx);
    const fy = cy - Math.floor(cy);
    const uAxis = Math.abs(fx - 0.5) > Math.abs(fy - 0.5) ? cy : cx;
    u0 = uAxis * scaleU - half * scaleU;
    u1 = uAxis * scaleU + half * scaleU;
    const vCenter = 1.0 - cz * V_TILE_SCALE;
    v0 = vCenter - half * V_TILE_SCALE;
    v1 = vCenter + half * V_TILE_SCALE;
  }
  return sampleAvg(tex, u0, v0, u1, v1);
}

/** Возвращает мировой центр кубика С УЧЁТОМ Pose чанка (translation + rotation
 *  вокруг pivot=центр XY). Используется для спавна debris на актуальной
 *  позиции — иначе осколки сыпались бы из исходной точки спавна стула, даже
 *  если он уже отлетел в сторону. */
export function cubeWorldCenter(c: VoxelChunkClient, idx: number): [number, number, number] {
  const bx = idx % c.sx;
  const by = Math.floor(idx / c.sx) % c.sy;
  const bz = Math.floor(idx / (c.sx * c.sy));
  const cs = c.cubeSize;
  // Локальные координаты центра кубика в frame чанка (origin в углу).
  const localCx = (bx + 0.5) * cs;
  const localCy = (by + 0.5) * cs;
  const localCz = (bz + 0.5) * cs;
  if (!c.dynamic) {
    return [c.ox + localCx, c.oy + localCy, c.oz + localCz];
  }
  // Dynamic: применяем pose (quaternion вокруг pivot=XY-центра чанка).
  const meshCenterX = c.sx * cs * 0.5;
  const meshCenterY = c.sy * cs * 0.5;
  const lx = localCx - meshCenterX;
  const ly = localCy - meshCenterY;
  const lz = localCz;
  const r = quatRotate(c.pose.quat, lx, ly, lz);
  return [
    c.ox + meshCenterX + r[0] + c.pose.pos[0],
    c.oy + meshCenterY + r[1] + c.pose.pos[1],
    c.oz + r[2] + c.pose.pos[2],
  ];
}

/** Quat (W,X,Y,Z) поворот 3D вектора v. Используется для рендера и для
 *  вычисления мировых позиций кубов dynamic чанка. */
export function quatRotate(
  q: [number, number, number, number],
  vx: number, vy: number, vz: number,
): [number, number, number] {
  const w = q[0], qx = q[1], qy = q[2], qz = q[3];
  const tx = 2 * (qy * vz - qz * vy);
  const ty = 2 * (qz * vx - qx * vz);
  const tz = 2 * (qx * vy - qy * vx);
  return [
    vx + w * tx + (qy * tz - qz * ty),
    vy + w * ty + (qz * tx - qx * tz),
    vz + w * tz + (qx * ty - qy * tx),
  ];
}

/** Quat умножение (a ∘ b). */
export function quatMul(
  a: [number, number, number, number],
  b: [number, number, number, number],
): [number, number, number, number] {
  return [
    a[0] * b[0] - a[1] * b[1] - a[2] * b[2] - a[3] * b[3],
    a[0] * b[1] + a[1] * b[0] + a[2] * b[3] - a[3] * b[2],
    a[0] * b[2] - a[1] * b[3] + a[2] * b[0] + a[3] * b[1],
    a[0] * b[3] + a[1] * b[2] - a[2] * b[1] + a[3] * b[0],
  ];
}

/** Интегрировать quat за dt по угловой скорости ω в world space. */
export function quatIntegrate(
  q: [number, number, number, number],
  w: [number, number, number],
  dt: number,
): [number, number, number, number] {
  const wq: [number, number, number, number] = [0, w[0], w[1], w[2]];
  const dq = quatMul(wq, q);
  const half = 0.5 * dt;
  const out: [number, number, number, number] = [
    q[0] + dq[0] * half,
    q[1] + dq[1] * half,
    q[2] + dq[2] * half,
    q[3] + dq[3] * half,
  ];
  // normalize
  const n = Math.hypot(out[0], out[1], out[2], out[3]);
  if (n < 1e-9) return [1, 0, 0, 0];
  const inv = 1 / n;
  return [out[0] * inv, out[1] * inv, out[2] * inv, out[3] * inv];
}

/** Convert quat → (axis, angle) для совместимости с шейдером, который
 *  ожидает axis-angle uniform для object rotation. */
export function quatToAxisAngle(
  q: [number, number, number, number],
): { axis: [number, number, number]; angle: number } {
  let w = q[0];
  if (w > 1) w = 1;
  else if (w < -1) w = -1;
  const angle = 2 * Math.acos(w);
  const s = Math.sqrt(1 - w * w);
  if (s < 1e-4) {
    return { axis: [0, 0, 1], angle: 0 };
  }
  return { axis: [q[1] / s, q[2] / s, q[3] / s], angle };
}
